/**
 * The activity log in words.
 *
 * The server records what happened as a short code ("price_changed") and a
 * bundle of facts. The owner reading the log should see "Changed a price:
 * ₪120.00 → ₪99.00", in their own language - not the code, and not the facts
 * as the programmer stored them.
 */
import type { Lang } from '@pos/shared';

const WORDS: Record<string, [string, string]> = {
  login: ['Signed in', 'دخل'],
  logout: ['Signed out', 'طلع'],
  login_failed: ['Wrong password', 'كلمة سر غلط'],
  login_refused_till_limit: ['Refused: too many tills', 'مرفوض: صناديق أكثر من المسموح'],
  password_changed: ['Changed their password', 'غيّر كلمة السر'],
  password_help_requested: ['Asked for a new password', 'طلب كلمة سر جديدة'],
  password_help_dismissed: ['Password request closed', 'انغلق طلب كلمة السر'],
  password_reset_by_system_owner: ['Password reset by the system owner', 'صاحب النظام غيّر كلمة السر'],
  manager_approval: ['Manager approved', 'المدير وافق'],
  approval_failed: ['Manager approval refused', 'موافقة المدير انرفضت'],
  sale_voided: ['Cancelled a sale', 'ألغى فاتورة'],
  receipt_renumbered: ['Receipt number changed', 'رقم الفاتورة تغيّر'],
  sync_partial_failure: ['Some sales did not upload', 'فواتير ما انرفعت'],
  shift_opened: ['Opened a shift', 'فتح وردية'],
  shift_closed: ['Closed a shift', 'سكّر وردية'],
  drawer_opened: ['Opened the drawer', 'فتح الدرج'],
  price_changed: ['Changed a price', 'غيّر سعر'],
  product_created: ['Added a product', 'زاد صنف'],
  product_archived: ['Archived a product', 'أرشف صنف'],
  product_photo: ['Changed a product photo', 'غيّر صورة صنف'],
  product_photo_removed: ['Removed a product photo', 'شال صورة صنف'],
  products_imported: ['Imported products', 'استورد أصناف'],
  barcode_added: ['Added a barcode', 'زاد باركود'],
  stock_received: ['Received stock', 'استلم بضاعة'],
  stock_adjusted: ['Adjusted stock', 'عدّل المخزون'],
  stock_count_applied: ['Applied a stock count', 'طبّق جرد'],
  stock_rebuilt: ['Recounted stock from the ledger', 'أعاد حساب المخزون'],
  purchase_payment: ['Paid a supplier', 'دفع لمورد'],
  purchase_payment_removed: ['Removed a supplier payment', 'شال دفعة مورد'],
  expense_added: ['Added an expense', 'زاد مصروف'],
  expense_deleted: ['Deleted an expense', 'حذف مصروف'],
  customer_paid: ['Customer paid their account', 'زبون سدّد حسابه'],
  customer_tab_changed: ['Changed a customer account', 'عدّل حساب زبون'],
  credit_issued: ['Issued store credit', 'أعطى رصيد محل'],
  giftcard_sold: ['Sold a gift card', 'باع بطاقة هدية'],
  promotion_created: ['Started a promotion', 'عمل عرض'],
  promotion_changed: ['Changed a promotion', 'عدّل عرض'],
  promotion_stopped: ['Stopped a promotion', 'وقّف عرض'],
  promotion_deleted: ['Removed a promotion', 'حذف عرض'],
  settings_changed: ['Changed settings', 'غيّر الإعدادات'],
  rate_changed: ['Changed an exchange rate', 'غيّر سعر صرف'],
  user_created: ['Added a staff member', 'زاد موظف'],
  user_changed: ['Changed a staff member', 'عدّل موظف'],
  user_permissions_changed: ["Changed someone's permissions", 'غيّر صلاحيات موظف'],
  role_created: ['Added a role', 'زاد دور'],
  role_changed: ['Changed a role', 'عدّل دور'],
  role_deleted: ['Deleted a role', 'حذف دور'],
  device_first_seen: ['A new till signed in', 'صندوق جديد دخل'],
  device_retired: ['Retired a till', 'وقّف صندوق'],
  device_restored: ['Brought a till back', 'رجّع صندوق'],
  backup_created: ['Made a backup', 'عمل نسخة احتياطية'],
};

export function auditLabel(action: string, lang: Lang): string {
  const w = WORDS[action];
  if (w) return lang === 'ar' ? w[1] : w[0];
  // Something newer than this list: at least not an underscore in sight.
  const plain = action.replace(/_/g, ' ');
  return plain.charAt(0).toUpperCase() + plain.slice(1);
}

type Money = (agorot: number) => string;

/** The facts that matter for this kind of entry, in a line a person can read. */
export function auditDetail(action: string, raw: unknown, lang: Lang, money: Money): string {
  let d: Record<string, unknown> = {};
  try {
    d = typeof raw === 'string' ? (JSON.parse(raw) as Record<string, unknown>) : ((raw as Record<string, unknown>) ?? {});
  } catch {
    return String(raw ?? '');
  }
  if (!d || typeof d !== 'object') return '';
  const ar = lang === 'ar';
  const n = (v: unknown) => (typeof v === 'number' ? v : Number(v));
  switch (action) {
    case 'price_changed':
      return `${money(n(d.from))} → ${money(n(d.to))}`;
    case 'sale_voided':
      return [d.receiptNo, d.total !== undefined ? money(n(d.total)) : '', d.reason].filter(Boolean).join(' · ');
    case 'expense_added':
      return [d.category, d.amount !== undefined ? money(n(d.amount)) : ''].filter(Boolean).join(' · ');
    case 'drawer_opened':
      return d.reason === 'no_sale' ? (ar ? 'بدون بيع' : 'No sale') : String(d.reason ?? '');
    case 'products_imported':
      return ar
        ? `${n(d.products)} صنف جديد، ${n(d.pieces)} قطعة، ${n(d.qty)} بالمخزون${d.file ? ` · ${d.file}` : ''}`
        : `${n(d.products)} new, ${n(d.pieces)} pieces, ${n(d.qty)} in stock${d.file ? ` · ${d.file}` : ''}`;
    case 'promotion_created':
    case 'promotion_changed':
      return String((ar ? d.nameAr || d.nameEn : d.nameEn || d.nameAr) ?? '');
    case 'login_failed':
    case 'password_help_requested':
      return String(d.username ?? '');
    case 'login_refused_till_limit':
      return `${d.username ?? ''} · ${ar ? 'الحد' : 'limit'} ${n(d.max)}`;
    case 'user_created':
      return [d.username, d.role].filter(Boolean).join(' · ');
    case 'barcode_added':
      return String(d.code ?? '');
    case 'customer_tab_changed':
      return [d.delta !== undefined ? money(n(d.delta)) : '', d.note].filter(Boolean).join(' · ');
    case 'manager_approval':
    case 'approval_failed':
      return [d.username, d.reason].filter(Boolean).join(' · ');
    case 'stock_adjusted':
      return [d.reason, Array.isArray(d.lines) ? `${d.lines.length} ${ar ? 'سطر' : 'lines'}` : ''].filter(Boolean).join(' · ');
    case 'login':
    case 'logout':
      return '';
    default:
      // Everything else: the plain facts, without the internal ids nobody can read.
      return Object.entries(d)
        .filter(([k, v]) => !/id$|Id$|^ip$/.test(k) && v !== null && v !== undefined && typeof v !== 'object')
        .map(([k, v]) => `${k}: ${v}`)
        .join(' · ');
  }
}
